/**
 * GameCarousel (#1029) — full-screen overlay letting the elderly user BROWSE
 * and pick an interactive tablet game (Solitaire / Dominoes).
 * Word/voice games never appear here — they have no board to show.
 *
 * Opened by Berta when the user asks to see the games: the server emits
 * [SHOW_GAMES], sends {type: games_menu_start, games:[…ordered…], personalised}
 * and the overlay opens (deferred until Berta finishes her line, same as the
 * game overlays). The games arrive ALREADY ordered most-played-first; this
 * component is purely presentational over that order. `personalised` (the
 * user has real play history) drives the gold "you enjoy this" badge on the
 * leading card — the "(when having)" preference clause of #1029.
 *
 * Interaction (elderly-accessible, per the product philosophy):
 *  - One game per screen — single focus, nothing to scan.
 *  - Big left/right arrows page through, WRAPPING (no dead-end disabled
 *    button to puzzle over). Keyboard ← → and swipe also work; arrows are
 *    the obvious primary control.
 *  - A word-based "Game 2 of 3" indicator + dots show position.
 *  - Big Play button → onPick(gameId). The server speaks a warm
 *    acknowledgement and opens that game's existing overlay.
 *  - A clear Close affordance (and Berta closing it via [CLEAR_MEDIA] on a
 *    voiced "never mind") dismisses without starting anything.
 *
 * Layout mirrors the game overlays: full-screen, CallStrip kept visible
 * (side panel on tablet-landscape, strip/pill otherwise) so call status +
 * hangup stay reachable. Card is laid out horizontally (art beside text) in
 * the short landscape modes, vertically (art above text) in portrait.
 *
 * Props:
 *   games          string[]  — ordered game ids (most-played first)
 *   personalised   boolean   — show the "you enjoy this" badge on games[0]
 *   onPick(id)               — user tapped Play on game `id`
 *   onClose()                — user dismissed the picker without choosing
 *   callStatus, callReply, callTranscript, companionName, onHangup — CallStrip
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useActivityBox } from './ActivityFrame';
import { useLang, formatString } from '../i18n/LanguageContext';

// Design tokens — match the live tablet UI (see SolitaireGame.js / mockup).
const CREAM = '#fff8f0';
const IVORY = '#FBF7EE';
const IVORY_EDGE = '#E8DCC0';
const ORANGE = '#E8845A';
const ORANGE_DEEP = '#DD6F3E';
const TEAL = '#047985';
const INK = '#2A2418';
const MUTED = '#6B6253';
const GOLD_BG = '#FFF1DB';
const GOLD_INK = '#B5701E';

// Canonical fallback order if the server payload is ever empty (defensive).
const DEFAULT_ORDER = ['solitaire', 'domino'];

// ── Per-game decorative art (static SVG; recognisable, not pixel-perfect) ──
function DominoPips({ n, cx, cy }) {
  const P = {
    1: [[0, 0]], 2: [[-1, -1], [1, 1]], 3: [[-1, -1], [0, 0], [1, 1]],
    4: [[-1, -1], [1, -1], [-1, 1], [1, 1]],
    5: [[-1, -1], [1, -1], [0, 0], [-1, 1], [1, 1]],
    6: [[-1, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [1, 1]],
  };
  return (P[n] || []).map(([x, y], i) => (
    <circle key={i} cx={cx + x * 15} cy={cy + y * 20} r={6.5} fill="#2A2418" />
  ));
}
function DominoTile({ x, y, rot, faces }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rot})`}>
      <rect x={-46} y={-86} width={92} height={172} rx={12} fill="#000" opacity={0.22} transform="translate(4 7)" />
      <rect x={-46} y={-86} width={92} height={172} rx={12} fill="#F8F0DC" stroke="#D5C896" strokeWidth={3} />
      <line x1={-40} y1={0} x2={40} y2={0} stroke="#D5C896" strokeWidth={3} />
      <DominoPips n={faces[0]} cx={0} cy={-43} />
      <DominoPips n={faces[1]} cx={0} cy={43} />
    </g>
  );
}
function DominoArt() {
  return (
    <svg viewBox="0 0 400 250" preserveAspectRatio="xMidYMid slice" role="img" aria-hidden="true">
      <defs>
        <linearGradient id="gcFelt1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#0d5e3d" /><stop offset="1" stopColor="#083f29" />
        </linearGradient>
      </defs>
      <rect width="400" height="250" fill="url(#gcFelt1)" />
      <DominoTile x={120} y={150} rot={-12} faces={[6, 3]} />
      <DominoTile x={255} y={118} rot={9} faces={[5, 5]} />
    </svg>
  );
}
function PlayingCard({ x, y, rot, rank, suit, color }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rot})`}>
      <rect x={-52} y={-74} width={104} height={148} rx={12} fill="#000" opacity={0.22} transform="translate(4 6)" />
      <rect x={-52} y={-74} width={104} height={148} rx={12} fill="#FBF7EE" stroke="#C9BE9A" strokeWidth={2.5} />
      <text x={-40} y={-46} fontFamily="Georgia, serif" fontSize={30} fontWeight={700} fill={color}>{rank}</text>
      <text x={0} y={22} fontSize={62} textAnchor="middle" fill={color}>{suit}</text>
    </g>
  );
}
function SolitaireArt() {
  return (
    <svg viewBox="0 0 400 250" preserveAspectRatio="xMidYMid slice" role="img" aria-hidden="true">
      <defs>
        <linearGradient id="gcFelt2" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#0d5e3d" /><stop offset="1" stopColor="#083f29" />
        </linearGradient>
      </defs>
      <rect width="400" height="250" fill="url(#gcFelt2)" />
      <PlayingCard x={150} y={140} rot={-16} rank="A" suit="♠" color="#1A1A1A" />
      <PlayingCard x={205} y={128} rot={0} rank="K" suit="♥" color="#B3261E" />
      <PlayingCard x={260} y={140} rot={16} rank="Q" suit="♣" color="#1A1A1A" />
    </svg>
  );
}
const ART = { domino: DominoArt, solitaire: SolitaireArt };

// ── i18n (with English fallbacks so a missing key never blanks the UI) ──
function _useStrings() {
  const ctx = useLang();
  const s = (ctx && ctx.t && ctx.t.tablet && ctx.t.tablet.gamecarousel) || {};
  const titles = s.titles || {};
  const descriptions = s.descriptions || {};
  return {
    header: s.header || 'What shall we play?',
    play: s.play || 'Play',
    position: s.position || 'Game {index} of {total}',
    favourite: s.favourite || 'You enjoy this',
    closeAria: s.closeAria || 'Close the game picker',
    prevAria: s.prevAria || 'Previous game',
    nextAria: s.nextAria || 'Next game',
    title: (id) => titles[id] || ({
      solitaire: 'Solitaire', domino: 'Dominoes',
    })[id] || id,
    desc: (id) => descriptions[id] || '',
  };
}

// ── arrow + close glyphs ──
const Chevron = ({ dir }) => (
  <svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" stroke="currentColor"
       strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {dir === 'left' ? <path d="M15 5l-7 7 7 7" /> : <path d="M9 5l7 7-7 7" />}
  </svg>
);

export default function GameCarousel({
  games, personalised, onPick,
}) {
  const { width: boxWidth, height: boxHeight } = useActivityBox();
  const tr = _useStrings();
  const list = (Array.isArray(games) && games.length) ? games : DEFAULT_ORDER;
  const n = list.length;
  const [index, setIndex] = useState(0);
  const go = useCallback((i) => setIndex(((i % n) + n) % n), [n]);  // wraps

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') go(index - 1);
      else if (e.key === 'ArrowRight') go(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, go]);

  // touch swipe (natural on a tablet); arrows remain the primary control.
  const touchX = useRef(null);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 45) go(index + (dx < 0 ? 1 : -1));
    touchX.current = null;
  };

  const current = list[index];
  const Art = ART[current] || SolitaireArt;
  // #1089 — layout flags derive from the MEASURED content box, not the
  // viewport: landscape = wider than tall; compact = a small box; short =
  // little vertical room (the header hides). isHorizontal stays as an alias
  // for the card's wide max-width. Fallback 700x700 before first measure.
  const bw = boxWidth || 700;
  const bh = boxHeight || 700;
  const isLandscape = bw > bh;
  const isHorizontal = isLandscape;
  const compact = bw < 520;
  const isShort = bh < 360;
  const showBadge = !!personalised && index === 0;

  const arrowSize = compact ? 60 : 80;
  const titleSize = compact ? 'clamp(26px, 7vw, 32px)' : 'clamp(30px, 4.2vw, 42px)';
  const descSize = compact ? 'clamp(16px, 4.2vw, 19px)' : 'clamp(18px, 2.4vw, 23px)';

  const card = (
    <div
      role="group"
      aria-roledescription="slide"
      aria-label={tr.title(current)}
      style={{
        position: 'relative', width: '100%', height: '100%',
        maxWidth: isHorizontal ? 1000 : 720, margin: '0 auto',
        background: IVORY, border: `2px solid ${IVORY_EDGE}`,
        borderRadius: compact ? 22 : 28,
        boxShadow: '0 18px 40px -18px rgba(74,51,20,.45)',
        padding: 'clamp(14px, 3vh, 28px)', boxSizing: 'border-box',
        display: 'flex', flexDirection: isLandscape ? 'row' : 'column',
        alignItems: 'center', gap: isLandscape ? 'clamp(16px,3vw,34px)' : 'clamp(10px,2vh,20px)',
        overflow: 'hidden',
      }}
    >
      {showBadge && (
        <div style={{
          position: 'absolute', top: 14, left: 14, display: 'inline-flex',
          alignItems: 'center', gap: 6, background: GOLD_BG, color: GOLD_INK,
          fontWeight: 700, fontSize: 'clamp(13px,1.5vw,16px)',
          padding: '7px 14px 7px 11px', borderRadius: 999, zIndex: 2,
          boxShadow: '0 2px 8px -2px rgba(181,112,30,.4)',
        }}>
          <svg viewBox="0 0 24 24" width="1.05em" height="1.05em" fill="currentColor" aria-hidden="true">
            <path d="M12 2.5l2.9 5.9 6.5.95-4.7 4.58 1.1 6.47L12 17.9 6.2 20.9l1.1-6.47L2.6 9.85l6.5-.95L12 2.5Z" />
          </svg>
          <span>{tr.favourite}</span>
        </div>
      )}
      <div style={{
        flex: isLandscape ? '1 1 52%' : '1 1 auto', width: isLandscape ? undefined : '100%',
        minHeight: 0, height: isLandscape ? '100%' : undefined,
        borderRadius: 18, overflow: 'hidden',
        boxShadow: '0 6px 16px -8px rgba(0,0,0,.4)',
        display: 'flex',
      }}>
        <Art />
      </div>
      <div style={{
        flex: isLandscape ? '1 1 48%' : '0 0 auto', width: isLandscape ? undefined : '100%',
        display: 'flex', flexDirection: 'column',
        alignItems: isLandscape ? 'flex-start' : 'center',
        justifyContent: 'center', textAlign: isLandscape ? 'left' : 'center',
        gap: 'clamp(8px, 1.6vh, 16px)',
      }}>
        <h2 style={{ margin: 0, fontSize: titleSize, fontWeight: 800, lineHeight: 1.05, color: INK }}>
          {tr.title(current)}
        </h2>
        <p style={{ margin: 0, fontSize: descSize, lineHeight: 1.35, color: MUTED, maxWidth: '32ch' }}>
          {tr.desc(current)}
        </p>
        <button
          onClick={() => onPick && onPick(current)}
          style={{
            marginTop: 2, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
            fontSize: compact ? 'clamp(20px,5vw,24px)' : 'clamp(24px,2.8vw,27px)',
            fontWeight: 800, color: '#fff',
            background: `linear-gradient(180deg, ${ORANGE}, ${ORANGE_DEEP})`,
            borderRadius: 999, padding: '.55em 1.6em', minHeight: compact ? 56 : 64,
            display: 'inline-flex', alignItems: 'center', gap: '.5em',
            boxShadow: '0 8px 18px -6px rgba(221,111,62,.7)',
          }}
        >
          <span style={{ fontSize: '.8em' }} aria-hidden="true">▶</span> {tr.play}
        </button>
      </div>
    </div>
  );

  const navBtnStyle = (side) => ({
    position: 'absolute', top: '50%', [side]: 0, transform: 'translateY(-50%)',
    width: arrowSize, height: arrowSize, borderRadius: '50%',
    border: '2px solid #ecdcc6', background: '#fff', color: TEAL, cursor: 'pointer',
    display: 'grid', placeItems: 'center', padding: arrowSize * 0.24,
    boxShadow: '0 8px 20px -8px rgba(74,51,20,.45)', zIndex: 3,
  });

  const carouselArea = (
    <div style={{
      flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column',
      padding: compact ? '10px 8px' : 'clamp(12px,3vh,26px) clamp(12px,3vw,30px)',
      boxSizing: 'border-box',
    }}>
      {/* header — hidden when the content box is short to reclaim height.
          Close (✕) is owned by the ActivityFrame now. */}
      {!isShort && (
        <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <h1 style={{ margin: 0, fontSize: compact ? 20 : 'clamp(20px,2.6vw,28px)', fontWeight: 800, color: INK }}>
            {tr.header}
          </h1>
        </div>
      )}

      {/* carousel viewport */}
      <div
        style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex',
                 alignItems: 'center', justifyContent: 'center',
                 padding: `0 ${arrowSize + 8}px` }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <button style={navBtnStyle('left')} onClick={() => go(index - 1)} aria-label={tr.prevAria}>
          <Chevron dir="left" />
        </button>
        <div style={{ width: '100%', height: '100%', display: 'flex' }}>{card}</div>
        <button style={navBtnStyle('right')} onClick={() => go(index + 1)} aria-label={tr.nextAria}>
          <Chevron dir="right" />
        </button>
      </div>

      {/* indicator */}
      <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 8, paddingTop: 8 }}>
        <div style={{ fontSize: compact ? 14 : 'clamp(14px,1.7vw,18px)', fontWeight: 700, color: MUTED }}>
          {formatString(tr.position, { index: index + 1, total: n })}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {list.map((g, i) => (
            <button
              key={g} onClick={() => go(i)} aria-label={tr.title(g)}
              style={{
                width: 12, height: 12, borderRadius: '50%', border: 'none', padding: 0,
                cursor: 'pointer', background: i === index ? ORANGE : '#e3d6bd',
                transform: i === index ? 'scale(1.35)' : 'none',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      // #1089 — fills the ActivityFrame content slot. Was fixed/inset with
      // a viewport-mode flex branch for the embedded CallStrip.
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      background: CREAM,
      display: 'flex',
      flexDirection: 'column',
      paddingTop: 'env(safe-area-inset-top)',
    }}>
      {carouselArea}
    </div>
  );
}
